// ─── 🌙 La partita e chi l'ha organizzata, nella scheda (fase 2, 26/09/2026) ──
//
// Dal mockup approvato («3 bottone, 4 organizzatore»):
//   · R12 — **chi manca lo paga l'organizzatore**: la quota di chi non c'è va
//     sulla sua riga, col bottone. Solo a partita cominciata: solo al circolo si
//     sa chi si è presentato davvero;
//   · 🚫 **«Segna no-show»** su ogni partita, anche di giorno e nel weekend:
//     conta sull'organizzatore. Di sera, con la caparra pagata, lo fa la sezione
//     della caparra (e la caparra resta al circolo);
//   · 🔒 il **lucchetto dei no-show ripetuti** (R16) di chi l'ha organizzata;
//   · ↩️ **«Annulla il no-show»** (voce 30), col perché: il lucchetto si
//     ricalcola da solo. Di sera, con la caparra trattenuta, lo fa la sezione
//     della caparra.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatCents,
  giornoPerEsteso,
  type OrganizzatoreDellaPartita,
  type SchedaPrenotazione,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Guasto } from '../../components/pezzi.js';

export function PartitaDellaScheda({
  p,
  puoIncassare,
}: {
  p: SchedaPrenotazione;
  puoIncassare: boolean;
}): React.JSX.Element | null {
  const qc = useQueryClient();
  const [conferma, setConferma] = useState(false);
  const [annulla, setAnnulla] = useState(false);
  const [motivo, setMotivo] = useState('');
  const dati = useQuery({
    queryKey: ['organizzatore-partita', p.id],
    queryFn: () => chiama<OrganizzatoreDellaPartita>('GET', `/bookings/${p.id}/organizer`),
    enabled: p.type === 'partita',
  });
  const rileggi = () => {
    void qc.invalidateQueries({ queryKey: ['organizzatore-partita', p.id] });
    void qc.invalidateQueries({ queryKey: ['prenotazione', p.id] });
    void qc.invalidateQueries({ queryKey: ['calendario'] });
  };
  const carica = useMutation({
    meta: { fatto: 'quote mancanti messe a carico dell’organizzatore' },
    mutationFn: () => chiama('POST', `/bookings/${p.id}/missing-to-organizer`),
    onSuccess: rileggi,
  });
  const noShow = useMutation({
    meta: { fatto: 'no-show segnato' },
    mutationFn: () => chiama('POST', `/bookings/${p.id}/no-show`),
    onSuccess: () => {
      setConferma(false);
      rileggi();
    },
  });

  const annullo = useMutation({
    meta: { fatto: 'no-show annullato' },
    mutationFn: () => chiama('POST', `/bookings/${p.id}/no-show/cancel`, { reason: motivo.trim() }),
    onSuccess: () => {
      setAnnulla(false);
      setMotivo('');
      rileggi();
    },
  });

  if (p.type !== 'partita' || !dati.data) return null;
  const {
    missing: q,
    noShow: segnato,
    organizerFlag: flag,
    noShowCancelled: annullato,
    cancelPreview: anteprima,
  } = dati.data;
  const cominciata = Date.parse(p.startsAt) <= Date.now();
  const c = p.caparraDettaglio;
  // Con la caparra pagata, il no-show è della sezione della caparra.
  const caparraViva = c !== null && c !== undefined && c.status === 'paid' && !c.appliedAt;
  // ↩️ Voce 30: la caparra trattenuta per il no-show si annulla dalla sezione della caparra.
  const caparraTrattenuta = c?.status === 'forfeited' && c.closeReason === 'no_show';
  const puoSegnare = puoIncassare && cominciata && !segnato && !caparraViva && p.status !== 'cancelled';
  const organizzatore = p.players.find((g) => g.memberId !== null && g.memberId === p.organizerMemberId);

  if (!q && !puoSegnare && !segnato && !flag?.active && !annullato) return null;

  return (
    <div className="caparra-scheda">
      {flag?.active && flag.until && (
        <p className="avvertenza">
          🔒 <strong>Acconto sempre, fino a {giornoPerEsteso(flag.until)}</strong> per chi l’ha organizzata:
          ha saltato {flag.dates.length} partite ({flag.dates.map((x) => giornoPerEsteso(x)).join(', ')}).
        </p>
      )}

      {q && cominciata && (
        <div className="finestra-caparra">
          {q.done ? (
            <p>
              ✓ La quota di chi manca è a carico di <strong>{q.organizerName}</strong>:{' '}
              {formatCents(q.organizerTotalCents)}.
            </p>
          ) : (
            <>
              <p>
                <strong>{q.missing === 1 ? 'Manca 1 giocatore' : `Mancano ${q.missing} giocatori`}.</strong>{' '}
                {q.missing === 1 ? 'La sua quota' : 'La loro quota'} ({q.missing} ×{' '}
                {formatCents(q.quotaCents)}) va a carico di chi ha organizzato.
              </p>
              {puoIncassare && (
                <div className="azioni-caparra">
                  <button type="button" disabled={carica.isPending} onClick={() => carica.mutate()}>
                    Metti {formatCents(q.quotaCents * q.missing)} a carico di {q.organizerName}
                  </button>
                </div>
              )}
              <Guasto errore={carica.error} />
            </>
          )}
        </div>
      )}

      {segnato && !caparraViva && !caparraTrattenuta && (
        <p className="tenue piccolo">🚫 Segnata come no-show.</p>
      )}
      {segnato && !caparraViva && !caparraTrattenuta && puoIncassare && !annulla && (
        <div className="azioni-caparra">
          <button type="button" className="bordato" onClick={() => setAnnulla(true)}>
            ↩️ Annulla il no-show
          </button>
        </div>
      )}
      {annulla && (
        <div className="finestra-caparra">
          <strong>↩️ Annulla il no-show</strong>
          <p>
            Il no-show non conta più su {organizzatore?.displayName ?? 'chi l’ha organizzata'}.
            {anteprima?.liftsFlag && (
              <>
                {' '}
                Senza questa partita scende a {anteprima.remaining} no-show, sotto la soglia di{' '}
                {anteprima.threshold}: <strong>il lucchetto cade</strong>, e il bot gli scrive che si torna
                come prima.
              </>
            )}
          </p>
          <label className="campo">
            <span>Perché lo annulli (resta scritto)</span>
            <input
              id="motivo-annullo-no-show"
              value={motivo}
              placeholder="per esempio: erano in campo, segnata per sbaglio"
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <div className="azioni-caparra">
            <button
              type="button"
              disabled={motivo.trim().length < 3 || annullo.isPending}
              onClick={() => annullo.mutate()}
            >
              Annulla il no-show
            </button>
            <button type="button" className="bordato" onClick={() => setAnnulla(false)}>
              Lascia stare
            </button>
          </div>
          <Guasto errore={annullo.error} />
        </div>
      )}
      {!segnato && annullato && (
        <p className="annullo-no-show">
          ↩️ No-show annullato il{' '}
          {new Date(annullato.at).toLocaleString('it-IT', {
            day: 'numeric',
            month: 'long',
            hour: '2-digit',
            minute: '2-digit',
          })}
          {annullato.by ? ` da ${annullato.by}` : ''}: «{annullato.reason}»
        </p>
      )}
      {puoSegnare && !conferma && (
        <div className="azioni-caparra">
          <button type="button" className="bordato" onClick={() => setConferma(true)}>
            🚫 Segna no-show
          </button>
        </div>
      )}
      {conferma && (
        <div className="finestra-caparra">
          <strong>🚫 Non si sono presentati</strong>
          <p>
            Conta su chi ha organizzato. Se arriva alla soglia di Impostazioni › Acconto della sera, per un
            periodo ogni sua prenotazione chiede l’acconto, e il bot glielo dice.
          </p>
          <div className="azioni-caparra">
            <button type="button" disabled={noShow.isPending} onClick={() => noShow.mutate()}>
              Segna no-show
            </button>
            <button type="button" className="bordato" onClick={() => setConferma(false)}>
              Lascia stare
            </button>
          </div>
          <Guasto errore={noShow.error} />
        </div>
      )}
    </div>
  );
}

/**
 * 🎓 🆕 Fase 3 (R6) — **la lezione della sera**: «🚫 L'allievo non si è
 * presentato» (30 € dal compenso del maestro), e la trattenuta già scritta,
 * che si toglie col motivo.
 */
export function LezioneDellaScheda({
  p,
  puoIncassare,
}: {
  p: SchedaPrenotazione;
  puoIncassare: boolean;
}): React.JSX.Element | null {
  const qc = useQueryClient();
  const [chiede, setChiede] = useState<'noshow' | 'togli' | null>(null);
  const [motivo, setMotivo] = useState('');
  const rileggi = () => {
    void qc.invalidateQueries({ queryKey: ['prenotazione', p.id] });
    void qc.invalidateQueries({ queryKey: ['conto-maestri'] });
  };
  const noShow = useMutation({
    meta: { fatto: 'segnato: l’allievo non si è presentato · 30 € dal compenso del maestro' },
    mutationFn: () => chiama('POST', `/bookings/${p.id}/no-show`),
    onSuccess: () => {
      setChiede(null);
      rileggi();
    },
  });
  const togli = useMutation({
    meta: { fatto: 'trattenuta al maestro tolta' },
    mutationFn: () =>
      chiama('POST', `/instructor-deductions/${p.trattenuta!.id}/remove`, { reason: motivo.trim() }),
    onSuccess: () => {
      setChiede(null);
      setMotivo('');
      rileggi();
    },
  });
  if (!p.lezioneSerale && !p.trattenuta) return null;
  const cominciata = Date.parse(p.startsAt) <= Date.now();
  const t = p.trattenuta ?? null;
  return (
    <div className="caparra-scheda">
      {t ? (
        <p className="avvertenza">
          🎓{' '}
          <strong>
            {formatCents(t.amountCents)} dal compenso di {p.instructorName ?? 'chi la tiene'}
          </strong>
          : {t.reason === 'no_show' ? 'l’allievo non si è presentato' : 'disdetta a meno di 48 ore'}.
        </p>
      ) : (
        <p className="tenue piccolo">
          🌙 Lezione della sera: se viene disdetta a meno di 48 ore, o l’allievo non si presenta, 30 € si
          tolgono dal compenso del maestro.
        </p>
      )}
      <div className="azioni-caparra">
        {puoIncassare && !t && cominciata && p.status === 'confirmed' && (
          <button type="button" className="bordato" onClick={() => setChiede('noshow')}>
            🚫 L’allievo non si è presentato
          </button>
        )}
        {puoIncassare && t && (
          <button type="button" className="bordato" onClick={() => setChiede('togli')}>
            Togli la trattenuta
          </button>
        )}
      </div>
      {chiede === 'noshow' && (
        <div className="finestra-caparra">
          <p>
            30 € si tolgono dal compenso di {p.instructorName ?? 'chi la tiene'}, e si vedono nel conto dei
            maestri e nella sua app.
          </p>
          <Guasto errore={noShow.error} />
          <div className="azioni-caparra">
            <button type="button" disabled={noShow.isPending} onClick={() => noShow.mutate()}>
              Segna
            </button>
            <button type="button" className="bordato" onClick={() => setChiede(null)}>
              Lascia stare
            </button>
          </div>
        </div>
      )}
      {chiede === 'togli' && (
        <div className="finestra-caparra">
          <label className="campo">
            <span>Perché la togli (resta scritto)</span>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </label>
          <Guasto errore={togli.error} />
          <div className="azioni-caparra">
            <button
              type="button"
              disabled={togli.isPending || motivo.trim().length < 3}
              onClick={() => togli.mutate()}
            >
              Togli la trattenuta
            </button>
            <button type="button" className="bordato" onClick={() => setChiede(null)}>
              Lascia stare
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
